import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dropzone", "fileRow", "filename", "fileMetadata", "fileStatus", "input", "requiredField", "submit"]
  static values = { existingFile: Boolean, draftUrl: String }

  connect() {
    this.updateState()
  }

  disconnect() {
    if (!this.submitting) this.saveDraftNow()
  }

  showProfileEditor(event) {
    event.target.closest('details').open = true
  }

  chooseFile() {
    this.inputTarget.click()
  }

  fileSelected() {
    const file = this.inputTarget.files?.[0]
    if (file) this.showFile(file.name)
    this.updateState()
  }

  dragover(event) {
    event.preventDefault()
    this.dropzoneTarget.dataset.dragging = "true"
  }

  dragleave(event) {
    event.preventDefault()
    delete this.dropzoneTarget.dataset.dragging
  }

  drop(event) {
    event.preventDefault()
    delete this.dropzoneTarget.dataset.dragging

    const file = event.dataTransfer.files?.[0]
    if (!file) return

    this.inputTarget.files = event.dataTransfer.files
    this.showFile(file.name)
    this.updateState()
  }

  removeFile() {
    this.inputTarget.value = ""
    this.existingFileValue = false
    this.fileRowTarget.hidden = true
    this.filenameTarget.textContent = ""
    this.fileMetadataTarget.hidden = true
    this.fileStatusTarget.textContent = "Select a customer file to continue"
    this.updateState()
  }

  updateState() {
    const fieldsComplete = this.requiredFieldTargets.every((field) => field.value.trim().length > 0)
    const filePresent = this.existingFileValue || this.inputTarget.files?.length > 0
    this.submitTarget.disabled = !(fieldsComplete && filePresent)
  }

  scheduleDraftSave() {
    if (this.submitting) return

    clearTimeout(this.draftTimer)
    this.draftTimer = setTimeout(() => this.saveDraft(), 400)
  }

  saveDraftNow() {
    if (this.submitting) return

    clearTimeout(this.draftTimer)
    this.saveDraft(true)
  }

  markSubmitting() {
    this.submitting = true
    clearTimeout(this.draftTimer)
    this.draftRequestController?.abort()
  }

  saveDraft(keepalive = false) {
    if (!this.hasDraftUrlValue) return

    const permittedNames = [
      "series_upload[setup_revision]",
      "upload[new_series_name]",
      "upload[snapshot_month]",
      "upload[snapshot_year]",
      "upload[snapshot_date]",
      "upload[upload_series_id]",
      "upload[best_customer_definition]",
      "upload[acquisition_path]",
      "series_context[cadence]",
      "series_context[golden_metric_label]",
      "series_context[retention_tracking]",
      "series_context[primary_outcome]"
    ]
    const body = new URLSearchParams()

    permittedNames.forEach((name) => {
      const field = this.element.elements.namedItem(name)
      if (field) body.append(name, field.value)
    })

    this.draftRequestController?.abort()
    const requestController = new AbortController()
    this.draftRequestController = requestController

    fetch(this.draftUrlValue, {
      method: "PATCH",
      credentials: "same-origin",
      keepalive,
      signal: requestController.signal,
      headers: {
        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
      },
      body
    }).catch(() => {}).finally(() => {
      if (this.draftRequestController === requestController) this.draftRequestController = null
    })
  }

  showFile(filename) {
    this.filenameTarget.textContent = filename
    this.fileMetadataTarget.hidden = true
    this.fileStatusTarget.textContent = `${filename} selected`
    this.fileRowTarget.hidden = false
  }
}
